"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { 
  X, 
  CheckCircle,
  Shield,
  Search,
  Filter
} from "lucide-react";
import { Input } from "../ui/input";

interface InternalControl {
  control_id: string;
  control_name: string;
  control_type: string;
  control_attribute: string;
  control_description: string;
}

interface ControlSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onControlsSelected: (selectedControls: InternalControl[]) => void;
  existingControls?: InternalControl[];
}

export default function ControlSelectorModal({ 
  isOpen, 
  onClose, 
  onControlsSelected,
  existingControls = []
}: ControlSelectorModalProps) {
  const [controls, setControls] = useState<InternalControl[]>([]);
  const [selectedControls, setSelectedControls] = useState<InternalControl[]>(existingControls);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterAttribute, setFilterAttribute] = useState<string>("all");

  // Load controls from Internal Control Library
  useEffect(() => {
    if (isOpen) {
      loadControls();
    }
  }, [isOpen]);

  const loadControls = async () => {
    setLoading(true);
    try {
      let libraryControls: InternalControl[] = [];

      // Preferred: load controls from Libraries_RCM.json in SharePoint (juggernaut folder)
      if (window.sharePointAPI && window.sharePointAPI.downloadRcmJson) {
        try {
          console.log("Loading controls from Libraries_RCM.json via SharePoint API...");
          const result = await window.sharePointAPI.downloadRcmJson();
          if (result.success && result.data) {
            const data = result.data;
            const librariesRcm = data.libraries_RCM || data.libraries_rcm || {};
            const entries = Object.entries(librariesRcm) as [string, any][];

            libraryControls = entries.map(([controlId, entry]) => {
              const typeOfControl = entry.TypeOfControl || entry.typeOfControl || "Unknown";
              const nature = entry.Nature || entry.subtype || "Unknown";
              const controlName = entry.control_name || entry.controlName || controlId;
              const description =
                entry.control_description ||
                entry.control_summary?.control_description ||
                `${controlName} - ${typeOfControl} control`;

              return {
                control_id: controlId,
                control_name: controlName,
                control_type: nature,
                control_attribute: typeOfControl,
                control_description: description,
              };
            });

            console.log("Loaded controls from Libraries_RCM.json:", libraryControls.length);
          } else {
            console.warn("downloadRcmJson did not return data:", result.error);
          }
        } catch (err) {
          console.error("Error loading controls from Libraries_RCM.json:", err);
        }
      }

      // Fallback to localStorage if no controls found (legacy support)
      if (libraryControls.length === 0) {
        console.log("=== FALLBACK TO LOCALSTORAGE (no entries in Libraries_RCM.json) ===");
        const savedControls = localStorage.getItem("internalControls");
        if (savedControls) {
          const parsedControls = JSON.parse(savedControls);
          libraryControls = parsedControls.map((control: any) => ({
            control_id: control.controlMetadata?.controlId || control.id,
            control_name: control.controlMetadata?.controlName || "Unknown Control",
            control_type: control.controlMetadata?.subtype === "Manual" ? "Manual" : "Automated",
            control_attribute: control.controlMetadata?.typeOfControl || "Unknown",
            control_description:
              control.control_summary?.control_description ||
              `${control.controlMetadata?.controlName || "Unknown"} - ${
                control.controlMetadata?.typeOfControl || "Unknown"
              } control`,
          }));
          console.log("Loaded from localStorage:", libraryControls.length, "controls");
        }
      }

      console.log("=== CONTROL SELECTOR DEBUG ===");
      console.log("Final controls loaded for selector:", libraryControls);
      console.log("Number of controls loaded:", libraryControls.length);
      console.log(
        "Controls details:",
        libraryControls.map((c) => ({
          id: c.control_id,
          name: c.control_name,
          type: c.control_type,
          attribute: c.control_attribute,
        }))
      );
      setControls(libraryControls);
    } catch (error) {
      console.error("Error loading controls:", error);
      setControls([]);
    } finally {
      setLoading(false);
    }
  };

  const handleControlToggle = (control: InternalControl) => {
    setSelectedControls(prev => {
      const isSelected = prev.some(c => c.control_id === control.control_id);
      if (isSelected) {
        return prev.filter(c => c.control_id !== control.control_id);
      } else {
        return [...prev, control];
      }
    });
  };

  const handleConfirm = () => {
    onControlsSelected(selectedControls);
    onClose();
  };

  const handleClose = () => {
    setSelectedControls(existingControls);
    setSearchTerm("");
    setFilterType("all");
    setFilterAttribute("all");
    onClose();
  };

  // Filter controls based on search and filters
  const filteredControls = controls.filter(control => {
    const matchesSearch = control.control_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         control.control_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         control.control_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === "all" || control.control_type === filterType;
    const matchesAttribute = filterAttribute === "all" || control.control_attribute === filterAttribute;
    
    return matchesSearch && matchesType && matchesAttribute;
  });

  // Get unique filter options
  const controlTypes = [...new Set(controls.map(c => c.control_type))];
  const controlAttributes = [...new Set(controls.map(c => c.control_attribute))];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-4xl max-h-[80vh] border-white/10 bg-black/80 text-white shadow-2xl overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between border-b border-white/10">
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#4da3ff]" />
            Associate Controls
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-white/60 hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6 overflow-hidden flex flex-col">
          {/* Search and Filters */}
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute top-2.5 left-3 h-4 w-4 text-white/40" />
              <Input
                placeholder="Search controls..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-white/10 bg-transparent pl-10 text-white placeholder:text-white/40 focus-visible:border-[#4da3ff]/60 focus-visible:ring-[#4da3ff]/40"
              />
            </div>
            
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-white/60" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="rounded border border-white/10 bg-white/5 px-2 py-1 text-sm text-white"
                >
                  <option value="all">All Types</option>
                  {controlTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <select
                  value={filterAttribute}
                  onChange={(e) => setFilterAttribute(e.target.value)}
                  className="rounded border border-white/10 bg-white/5 px-2 py-1 text-sm text-white"
                >
                  <option value="all">All Attributes</option>
                  {controlAttributes.map(attr => (
                    <option key={attr} value={attr}>{attr}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Selected Controls Summary */}
          {selectedControls.length > 0 && (
            <div className="bg-[#4da3ff]/10 border border-[#4da3ff]/20 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-[#4da3ff]" />
                <span className="text-sm font-medium text-[#4da3ff]">
                  {selectedControls.length} control(s) selected
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedControls.map(control => (
                  <Badge key={control.control_id} variant="outline" className="text-xs">
                    {control.control_id}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Controls List */}
          <div className="flex-1 overflow-y-auto space-y-2">
            {loading ? (
              <div className="text-center text-white/60 py-8">
                Loading controls...
              </div>
            ) : filteredControls.length === 0 ? (
              <div className="text-center text-white/60 py-8">
                No controls found matching your criteria
              </div>
            ) : (
              filteredControls.map((control) => {
                const isSelected = selectedControls.some(c => c.control_id === control.control_id);
                
                return (
                  <Card
                    key={control.control_id}
                    className={`cursor-pointer border transition-all ${
                      isSelected
                        ? "border-[#4da3ff]/50 bg-[#4da3ff]/10"
                        : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
                    }`}
                    onClick={() => handleControlToggle(control)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleControlToggle(control)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-sm font-medium text-white">
                              {control.control_name}
                            </h4>
                            <Badge variant="outline" className="text-xs">
                              {control.control_id}
                            </Badge>
                          </div>
                          <p className="text-xs text-white/70 mb-2">
                            {control.control_description}
                          </p>
                          <div className="flex gap-2">
                            <Badge variant="outline" className="text-xs">
                              {control.control_type}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {control.control_attribute}
                            </Badge>
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle className="h-5 w-5 text-[#4da3ff] mt-1" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 border-t border-white/10 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              className="border-white/10 bg-transparent text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80"
            >
              Associate {selectedControls.length} Control(s)
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
